import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import '../../styles/UserDetailsPage.css';

const UserDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalAction, setModalAction] = useState('');  // 'status' or 'delete'

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/users/${id}`);
        
        if (response.data) {
          // Handle different response formats
          const userData = response.data.success ? response.data.data : response.data;
          setUser(userData);
        } else {
          setError('User data not found');
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  const handleToggleActive = async () => {
    try {
      await api.put(`/users/${id}`, { 
        active: !user.active 
      });
      setUser(prev => ({ ...prev, active: !prev.active }));
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Error updating user status:', err);
      alert('Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await api.delete(`/users/${id}`);
      setShowConfirmModal(false);
      alert('User deleted successfully');
      navigate('/users');  // Redirect to users list
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    try {
      await api.post(`/auth/reset-password-admin/${id}`);
      alert('Password reset email has been sent to the user');
    } catch (err) {
      console.error('Error resetting password:', err);
      alert('Failed to reset password');
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'badge-admin';
      case 'teacher': return 'badge-teacher';
      case 'student': return 'badge-student';
      case 'parent': return 'badge-parent';
      case 'super_admin': return 'badge-super-admin';
      default: return 'badge-default';
    }
  };

  return (
    <div className="user-details-container">
      <div className="header">
        <h1>User Details</h1>
        <div className="header-actions">
          <Link to="/users" className="back-btn">
            Back to Users
          </Link>
          <Link to={`/users/${id}/edit`} className="edit-user-btn">
            Edit User
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading user details...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : user ? (
        <div className="user-details-content">
          <div className="user-info-card">
            <div className="user-header">
              <div className="avatar">
                {user.name ? user.name.charAt(0).toUpperCase() : '?'}
              </div>
              <div className="user-title">
                <h2>{user.name}</h2>
                <div className="user-meta">
                  <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                    {user.role}
                  </span>
                  <span className={`status-badge ${user.active ? 'status-active' : 'status-inactive'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-item">
                <span className="label">Email:</span>
                <span className="value">{user.email}</span>
              </div>
              <div className="info-item">
                <span className="label">Phone:</span>
                <span className="value">{user.phone || 'Not provided'}</span>
              </div>
              <div className="info-item">
                <span className="label">Center:</span>
                <span className="value">{user.center ? user.center.name || user.center : 'Not assigned'}</span>
              </div>
              <div className="info-item">
                <span className="label">Created:</span>
                <span className="value">{new Date(user.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="label">Last Updated:</span>
                <span className="value">{new Date(user.updatedAt).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Role-specific information */}
            {user.role === 'teacher' && user.teacherInfo && (
              <div className="role-specific-info">
                <h3>Teacher Information</h3>
                <div className="info-item">
                  <span className="label">Assigned Classes:</span>
                  <span className="value">
                    {user.teacherInfo.halaqas && user.teacherInfo.halaqas.length > 0 
                      ? user.teacherInfo.halaqas.length 
                      : 'None'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Specializations:</span>
                  <span className="value">{user.teacherInfo.specializations || 'Not specified'}</span>
                </div>
              </div>
            )}

            {user.role === 'student' && user.studentInfo && (
              <div className="role-specific-info">
                <h3>Student Information</h3>
                <div className="info-item">
                  <span className="label">Age:</span>
                  <span className="value">{user.studentInfo.age || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Grade:</span>
                  <span className="value">{user.studentInfo.grade || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Class:</span>
                  <span className="value">
                    {user.studentInfo.halaqa 
                      ? (typeof user.studentInfo.halaqa === 'object' 
                         ? user.studentInfo.halaqa.name 
                         : user.studentInfo.halaqa)
                      : 'Not assigned'}
                  </span>
                </div>
              </div>
            )}

            {user.role === 'parent' && user.parentInfo && (
              <div className="role-specific-info">
                <h3>Parent Information</h3>
                <div className="info-item">
                  <span className="label">Children:</span>
                  <span className="value">
                    {user.parentInfo.children && user.parentInfo.children.length > 0 
                      ? user.parentInfo.children.length 
                      : 'None'}
                  </span>
                </div>
              </div>
            )}

            <div className="actions-section">
              <button 
                className={`status-toggle-btn ${user.active ? 'deactivate' : 'activate'}`}
                onClick={() => {
                  setModalAction('status');
                  setShowConfirmModal(true);
                }}
              >
                {user.active ? 'Deactivate User' : 'Activate User'}
              </button>
              <button 
                className="reset-password-btn"
                onClick={handleResetPassword}
              >
                Reset Password
              </button>
              <button 
                className="delete-user-btn"
                onClick={() => {
                  setModalAction('delete');
                  setShowConfirmModal(true);
                }}
              >
                Delete User
              </button>
            </div>
          </div>

          {/* Additional sections based on role, like classes, students, etc. */}
          {user.role === 'teacher' && (
            <div className="additional-info-card">
              <h3>Assigned Classes</h3>
              {/* Classes list would go here */}
              <p className="placeholder-text">Class list will be displayed here</p>
            </div>
          )}

          {user.role === 'parent' && (
            <div className="additional-info-card">
              <h3>Children</h3>
              {/* Children list would go here */}
              <p className="placeholder-text">Children list will be displayed here</p>
            </div>
          )}
        </div>
      ) : (
        <div className="not-found">User not found</div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            {modalAction === 'status' ? (
              <>
                <h3>
                  {user.active 
                    ? 'Are you sure you want to deactivate this user?' 
                    : 'Are you sure you want to activate this user?'}
                </h3>
                <p>
                  {user.active 
                    ? 'The user will no longer be able to access the system.' 
                    : 'The user will be able to access the system again.'}
                </p>
                <div className="modal-actions">
                  <button 
                    className="cancel-btn"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className={`confirm-btn ${user.active ? 'danger' : 'success'}`}
                    onClick={handleToggleActive}
                  >
                    Confirm
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>Are you sure you want to delete this user?</h3>
                <p>This action cannot be undone. All data associated with this user will be permanently deleted.</p>
                <div className="modal-actions">
                  <button 
                    className="cancel-btn"
                    onClick={() => setShowConfirmModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    className="confirm-btn danger"
                    onClick={handleDeleteUser}
                  >
                    Delete User
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailsPage;